/**
 * Voice-note bubble player (screen-local, v3 "AFTER-DARK ARCADE").
 *
 * #55 voice notes, playback side: a tactile play/pause + a waveform that
 * lights up as the note plays. The waveform is a deterministic sketch derived
 * from the message id (the server stores no PCM peaks — this is presentation,
 * honest about being one). Same data contract as the old bare <audio>: it
 * plays the signed `media_url`, nothing else. The container keeps the
 * `.bubble__voice` class the e2e pins.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { resolveCopy } from "@humble/shared";
import { IconPause, IconPlay } from "./icons.js";

const BAR_COUNT = 26;

/** Deterministic 0..1 series from a string seed (xorshift over a djb2 hash). */
function pseudoWave(seed: string): number[] {
  let h = 5381;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) + h + seed.charCodeAt(i)) | 0;
  let x = h || 0x9e3779b9;
  const out: number[] = [];
  for (let i = 0; i < BAR_COUNT; i++) {
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    // keep bars in a talky 0.25..1 band with a soft center swell
    const r = (Math.abs(x) % 1000) / 1000;
    const swell = 0.75 + 0.25 * Math.sin((i / (BAR_COUNT - 1)) * Math.PI);
    out.push((0.25 + 0.75 * r) * swell);
  }
  return out;
}

function fmt(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function VoiceNote({
  src,
  seed,
  className,
}: {
  src?: string;
  seed: string;
  className?: string;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [clock, setClock] = useState("0:00");
  const bars = useMemo(() => pseudoWave(seed), [seed]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => {
      const d = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : NaN;
      setProgress(Number.isFinite(d) ? Math.min(1, el.currentTime / d) : 0);
      setClock(fmt(el.currentTime));
    };
    const onEnd = () => {
      setPlaying(false);
      setProgress(0);
      setClock(fmt(Number.isFinite(el.duration) ? el.duration : 0));
    };
    const onMeta = () => {
      if (Number.isFinite(el.duration)) setClock(fmt(el.duration));
    };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnd);
    el.addEventListener("loadedmetadata", onMeta);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnd);
      el.removeEventListener("loadedmetadata", onMeta);
      el.pause();
    };
  }, [src]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el || !src) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      void el.play().then(
        () => setPlaying(true),
        () => setPlaying(false),
      );
    }
  };

  const litUntil = Math.round(progress * BAR_COUNT);

  return (
    <span className={`voice-note${className ? ` ${className}` : ""}`}>
      <audio ref={audioRef} src={src} preload="metadata" hidden />
      <button
        type="button"
        className="voice-note__toggle"
        disabled={!src}
        aria-label={
          playing
            ? (resolveCopy("chat.voice_pause") ?? "Pause voice note")
            : (resolveCopy("chat.voice_play") ?? "Play voice note")
        }
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
      >
        {playing ? <IconPause width={16} height={16} /> : <IconPlay width={16} height={16} />}
      </button>
      <span className={`voice-note__wave${playing ? " voice-note__wave--playing" : ""}`} aria-hidden="true">
        {bars.map((h, i) => (
          <i
            key={i}
            className={i < litUntil ? "voice-note__bar voice-note__bar--lit" : "voice-note__bar"}
            style={{ transform: `scaleY(${h})` }}
          />
        ))}
      </span>
      <span className="voice-note__clock type-numeric">{clock}</span>
    </span>
  );
}
